import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc } from 'firebase/firestore';
import AdminLayout from '../../components/admin/AdminLayout';
import { useTenantCollection, useTenantCrud } from '../../hooks/useTenantData';
import { useClub } from '../../contexts/ClubContext';

function TakeAttendance() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { club, clubId } = useClub();
  
  // Get event details
  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  
  // Get players and existing attendance
  const { data: players, loading: loadingPlayers } = useTenantCollection('players');
  const { data: allAttendance, loading: loadingAttendance, setData: setAllAttendance } = useTenantCollection('attendance');
  const { add: addAttendance, update: updateAttendance } = useTenantCrud('attendance');
  
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Attendance records for this event
  const [attendanceRecords, setAttendanceRecords] = useState({});
  
  // Load event details
  useEffect(() => {
    const loadEvent = async () => {
      if (!eventId || !clubId) return;
      
      try {
        const eventDoc = await getDoc(doc(db, 'events', eventId));
        if (eventDoc.exists()) {
          const eventData = { id: eventDoc.id, ...eventDoc.data() };
          // Verify event belongs to this club
          if (eventData.clubId === clubId) {
            setEvent(eventData);
          } else {
            setError('Event not found');
          }
        } else {
          setError('Event not found');
        }
      } catch (err) {
        console.error('Error loading event:', err);
        setError('Failed to load event');
      } finally {
        setLoadingEvent(false);
      }
    };
    
    loadEvent();
  }, [eventId, clubId]);
  
  // Initialize attendance records when data loads
  useEffect(() => {
    if (!loadingAttendance && eventId) {
      // Get existing attendance for this event
      const existingAttendance = allAttendance.filter(a => a.eventId === eventId);
      const records = {};
      
      existingAttendance.forEach(record => {
        records[record.playerId] = {
          id: record.id,
          status: record.status,
          notes: record.notes || ''
        };
      });
      
      setAttendanceRecords(records);
    }
  }, [allAttendance, loadingAttendance, eventId]);
  
  // Get players for this event's team (or all players if no team specified)
  const eventPlayers = event?.team 
    ? players.filter(p => p.teamId === event.team || p.teamName === event.team)
    : players;
  
  const handleStatusChange = (playerId, status) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        status
      }
    }));
  };
  
  const handleNotesChange = (playerId, notes) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        notes
      }
    }));
  };
  
  const saveAttendance = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const updates = [];
      
      for (const playerId of Object.keys(attendanceRecords)) {
        const record = attendanceRecords[playerId];
        if (!record.status) continue; // Skip if no status selected
        
        const attendanceData = {
          eventId,
          playerId,
          status: record.status,
          notes: record.notes || '',
          eventDate: event.date,
          eventType: event.type,
          eventTitle: event.title
        };
        
        if (record.id) {
          // Update existing record
          await updateAttendance(record.id, attendanceData);
        } else {
          // Create new record
          const newRecord = await addAttendance(attendanceData);
          record.id = newRecord.id;
        }
        updates.push(attendanceData);
      }
      
      setSuccess(`Attendance saved for ${updates.length} players!`);
      
      // Update local state
      const updatedAttendance = allAttendance.filter(a => a.eventId !== eventId);
      updates.forEach(u => {
        updatedAttendance.push({ ...u, clubId });
      });
      setAllAttendance(updatedAttendance);
      
    } catch (err) {
      console.error('Error saving attendance:', err);
      setError('Failed to save attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  // Calculate stats
  const stats = {
    total: eventPlayers.length,
    present: Object.values(attendanceRecords).filter(r => r.status === 'present').length,
    absent: Object.values(attendanceRecords).filter(r => r.status === 'absent').length,
    excused: Object.values(attendanceRecords).filter(r => r.status === 'excused').length,
    unmarked: eventPlayers.length - Object.values(attendanceRecords).filter(r => r.status).length
  };
  
  const loading = loadingEvent || loadingPlayers || loadingAttendance;
  
  if (loading) {
    return (
      <AdminLayout title="Take Attendance" subtitle="Loading...">
        <div className="loading-container">
          <p>Loading attendance data...</p>
        </div>
      </AdminLayout>
    );
  }
  
  if (error && !event) {
    return (
      <AdminLayout title="Take Attendance" subtitle="Error">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => navigate('/admin/schedule')} className="btn-back">
            Back to Schedule
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Take Attendance" 
      subtitle={event ? `${event.title} - ${event.date}` : 'Loading...'}
    >
      <div className="attendance-page">
        {/* Event Info */}
        <div className="attendance-event-info">
          <div className="event-detail">
            <span className="label">Event:</span>
            <span className="value">{event?.title}</span>
          </div>
          <div className="event-detail">
            <span className="label">Date:</span>
            <span className="value">{event?.date}</span>
          </div>
          <div className="event-detail">
            <span className="label">Time:</span>
            <span className="value">{event?.time}</span>
          </div>
          <div className="event-detail">
            <span className="label">Location:</span>
            <span className="value">{event?.location || 'TBD'}</span>
          </div>
          <div className="event-detail">
            <span className="label">Team:</span>
            <span className="value">{event?.teamName || event?.team || 'All Teams'}</span>
          </div>
        </div>
        
        {/* Stats */}
        <div className="attendance-stats">
          <div className="stat-card total">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total Players</span>
          </div>
          <div className="stat-card present">
            <span className="stat-number">{stats.present}</span>
            <span className="stat-label">Present</span>
          </div>
          <div className="stat-card absent">
            <span className="stat-number">{stats.absent}</span>
            <span className="stat-label">Absent</span>
          </div>
          <div className="stat-card excused">
            <span className="stat-number">{stats.excused}</span>
            <span className="stat-label">Excused</span>
          </div>
          <div className="stat-card unmarked">
            <span className="stat-number">{stats.unmarked}</span>
            <span className="stat-label">Unmarked</span>
          </div>
        </div>
        
        {/* Messages */}
        {error && <div className="form-error">{error}</div>}
        {success && <div className="form-success">{success}</div>}
        
        {/* Quick Actions */}
        <div className="attendance-quick-actions">
          <button 
            className="btn-quick present"
            onClick={() => {
              const newRecords = {};
              eventPlayers.forEach(p => {
                newRecords[p.id] = { ...attendanceRecords[p.id], status: 'present' };
              });
              setAttendanceRecords(newRecords);
            }}
          >
            Mark All Present
          </button>
          <button 
            className="btn-quick absent"
            onClick={() => {
              const newRecords = {};
              eventPlayers.forEach(p => {
                newRecords[p.id] = { ...attendanceRecords[p.id], status: 'absent' };
              });
              setAttendanceRecords(newRecords);
            }}
          >
            Mark All Absent
          </button>
          <button 
            className="btn-quick clear"
            onClick={() => {
              const newRecords = {};
              eventPlayers.forEach(p => {
                newRecords[p.id] = { ...attendanceRecords[p.id], status: '' };
              });
              setAttendanceRecords(newRecords);
            }}
          >
            Clear All
          </button>
        </div>
        
        {/* Player List */}
        {eventPlayers.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">👥</span>
            <h3>No Players Found</h3>
            <p>No players are assigned to this team yet.</p>
          </div>
        ) : (
          <div className="attendance-list">
            {eventPlayers.map(player => (
              <div key={player.id} className="attendance-row">
                <div className="player-info">
                  <span className="player-number">#{player.jerseyNumber || '?'}</span>
                  <div className="player-details">
                    <span className="player-name">{player.firstName} {player.lastName}</span>
                    <span className="player-position">{player.position || 'Player'}</span>
                  </div>
                </div>
                <div className="attendance-buttons">
                  <button
                    className={`btn-attendance present ${attendanceRecords[player.id]?.status === 'present' ? 'active' : ''}`}
                    onClick={() => handleStatusChange(player.id, 'present')}
                  >
                    Present
                  </button>
                  <button
                    className={`btn-attendance absent ${attendanceRecords[player.id]?.status === 'absent' ? 'active' : ''}`}
                    onClick={() => handleStatusChange(player.id, 'absent')}
                  >
                    Absent
                  </button>
                  <button
                    className={`btn-attendance excused ${attendanceRecords[player.id]?.status === 'excused' ? 'active' : ''}`}
                    onClick={() => handleStatusChange(player.id, 'excused')}
                  >
                    Excused
                  </button>
                </div>
                <div className="attendance-notes">
                  <input
                    type="text"
                    placeholder="Notes (optional)"
                    value={attendanceRecords[player.id]?.notes || ''}
                    onChange={(e) => handleNotesChange(player.id, e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Save Button */}
        <div className="attendance-footer">
          <button 
            className="btn-back"
            onClick={() => navigate('/admin/schedule')}
          >
            Back to Schedule
          </button>
          <button 
            className="btn-save"
            onClick={saveAttendance}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Attendance'}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}

export default TakeAttendance;
